/**
 * @description Laptop
 * @author      C. M. de Picciotto <d3p1@d3p1.dev> (https://d3p1.dev/)
 * @note        Auto-generated by: https://github.com/pmndrs/gltfjsx
 * @note        Command: `npx gltfjsx@6.5.3 -t public/model/laptop.gltf`
 */
import {useFrame} from '@react-three/fiber'
import React, {useEffect, useRef} from 'react'
import {Html, useGLTF} from '@react-three/drei'
import type {GLTFResult} from '../../types'

export const Laptop = (props: React.JSX.IntrinsicElements['group']) => {
  const {nodes, materials} = useGLTF(
    '/r3f-computer-webcam-effect/model/laptop.gltf',
  ) as unknown as GLTFResult

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const videoRef = useRef<HTMLVideoElement>(document.createElement('video'))

  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({video: true, audio: false})
      .then((stream) => {
        videoRef.current.srcObject = stream
        videoRef.current.play().catch((e) => console.error(e))
      })
      .catch((e) => console.error(e))
  }, [])

  useFrame(() => {
    if (!videoRef.current.readyState || !canvasRef.current) {
      return
    }

    const ctx = canvasRef.current.getContext('2d') as CanvasRenderingContext2D
    ctx.drawImage(videoRef.current, 0, 0, ctx.canvas.width, ctx.canvas.height)

    const imgData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height)
    for (let i = 0; i < imgData.data.length; i += 4) {
      const r = 255 - imgData.data[i + 0]
      const g = 255 - imgData.data[i + 1]
      const b = 255 - imgData.data[i + 2]

      imgData.data[i + 0] = r
      imgData.data[i + 1] = g
      imgData.data[i + 2] = b
    }
    ctx.putImageData(imgData, 0, 0)
  })

  return (
    <group {...props} dispose={null}>
      <group position={[0, 0.519, 0]} scale={0.103}>
        <mesh
          geometry={nodes.Circle001.geometry}
          material={materials['Frame.001']}
        />
        <mesh
          geometry={nodes.Circle001_1.geometry}
          material={materials['Frame.001']}
        />
        <mesh
          geometry={nodes.Circle001_2.geometry}
          material={materials.HeadPhoneHole}
        />
        <mesh
          geometry={nodes.Circle001_3.geometry}
          material={materials.USB_C_INSIDE}
        />
        <mesh
          geometry={nodes.Circle001_4.geometry}
          material={materials['Frame.001']}
        />
        <mesh
          geometry={nodes.Circle001_5.geometry}
          material={materials.TouchbarBorder}
        />
        <mesh
          geometry={nodes.Circle001_6.geometry}
          material={materials.Keyboard}
        />
        <group position={[0, -0.509, 0]} scale={5.796}>
          <mesh
            geometry={nodes.Circle006.geometry}
            material={materials['Frame.001']}
          />
          <mesh
            geometry={nodes.Circle006_1.geometry}
            material={materials.USB_C_INSIDE}
          />
        </group>
        <mesh
          geometry={nodes.FrontCameraRing001.geometry}
          material={materials['CameraRIngBlack.002']}
          position={[-0.155, 19.571, -16.151]}
          scale={5.796}
        />
        <group position={[-11.786, -0.15, -8.301]} scale={5.796}>
          <mesh
            geometry={nodes.Circle.geometry}
            material={materials['Keyboard.001']}
          />
          <mesh geometry={nodes.Circle_1.geometry} material={materials.Key} />
          <mesh
            geometry={nodes.Circle_2.geometry}
            material={materials.Touchbar}
          />
        </group>
        <mesh
          geometry={nodes.KeyboardKeyHole.geometry}
          material={materials['Keyboard.001']}
          position={[-11.786, -0.152, -8.301]}
          scale={5.796}
        />
        <mesh
          geometry={nodes.RubberFoot.geometry}
          material={materials.DarkRubber}
          position={[-11.951, -0.751, 7.857]}
          scale={5.796}
        />
        <group position={[0.011, -0.211, -10.559]} scale={5.796}>
          <mesh
            geometry={nodes.Circle012.geometry}
            material={materials.HingeBlack}
          />
          <mesh
            geometry={nodes.Circle012_1.geometry}
            material={materials.HingeMetal}
          />
        </group>
        <group position={[-15.026, 0.031, 0.604]} scale={5.796}>
          <mesh
            geometry={nodes.Circle009.geometry}
            material={materials['Frame.001']}
          />
          <mesh
            geometry={nodes.Circle009_1.geometry}
            material={materials.SpeakerHole}
          />
        </group>
        <group position={[12.204, 0.031, 0.604]} scale={5.796}>
          <mesh
            geometry={nodes.Circle003.geometry}
            material={materials['Frame.001']}
          />
          <mesh
            geometry={nodes.Circle003_1.geometry}
            material={materials.SpeakerHole}
          />
        </group>
        <group
          position={[0.007, -0.472, -10.412]}
          rotation={[1.311, 0, 0]}
          scale={5.796}
        >
          <mesh
            geometry={nodes.Circle002.geometry}
            material={materials['Frame.001']}
          />
          <mesh
            geometry={nodes.Circle002_1.geometry}
            material={materials.Screen}
          />

          <mesh
            geometry={nodes.Circle002_2.geometry}
            material={materials.ScreenGlass}
          >
            <rectAreaLight
              intensity={4}
              position={[0, 0, -0.1]}
              rotation-y={Math.PI}
            />
            <Html
              transform={true}
              position={[0, 0, -1.9]}
              rotation-x={[-Math.PI / 2]}
              distanceFactor={1.95}
            >
              <canvas
                width={1024}
                height={670}
                ref={canvasRef}
                className="border-none rounded-md"
              />
            </Html>
          </mesh>

          <mesh
            geometry={nodes.Circle002_3.geometry}
            material={materials.Rubber}
          />
          <mesh
            geometry={nodes.Circle002_4.geometry}
            material={materials.DisplayGlass}
          />
          <mesh
            geometry={nodes.AppleLogo000.geometry}
            material={materials['AppleLogo.004']}
            position={[0.005, -0.111, -1.795]}
            rotation={[-Math.PI, 0, -Math.PI]}
            scale={0.579}
          />
        </group>
      </group>
    </group>
  )
}

useGLTF.preload('/r3f-computer-webcam-effect/model/laptop.gltf')
